import { useEffect } from "react";

const AFFILIATE_KEY = "9b6c1aeeac2e721347697c0b6775a5bd";
const AFFILIATE_TIMESTAMP_KEY = "ef92b7d895bd11171a8f4f84124416be";
const COOKIE_DURATION_MS = 30 * 24 * 60 * 60 * 1000; // 30 days in milliseconds

// Store the initial URL parameters globally to prevent race conditions
let initialUrlParams = null;
if (!initialUrlParams && typeof window !== 'undefined') {
  initialUrlParams = new URLSearchParams(window.location.search);
}

// Helper function to set a cookie that's accessible across all subdomains and paths
const setCookie = (name, value, days) => {
  const expires = new Date();
  expires.setTime(expires.getTime() + days * 24 * 60 * 60 * 1000);

  // Set cookie for the root path to ensure it's accessible across all apps
  if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};path=/;SameSite=Lax`;
  } else {
    document.cookie = `${name}=${value};expires=${expires.toUTCString()};domain=${process.env.REACT_APP_URL_FRONTEND_CHECKOUT.replace(
      /https:\/\/checkout/g,
      ""
    )};path=/;SameSite=Lax`;
  }
};

// Helper function to get a cookie value
const getCookie = (name) => {
  const nameEQ = `${name}=`;
  const ca = document.cookie.split(";");
  for (let i = 0; i < ca.length; i += 1) {
    let c = ca[i];
    while (c.charAt(0) === " ") c = c.substring(1, c.length);
    if (c.indexOf(nameEQ) === 0) return c.substring(nameEQ.length, c.length);
  }
  return null;
};

// Helper function to delete a cookie
const deleteCookie = (name) => {
  // Set cookie for the root path to ensure it's accessible across all apps
  if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;path=/;`;
  } else {
    document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 UTC;domain=${process.env.REACT_APP_URL_FRONTEND_CHECKOUT.replace(
      /https:\/\/checkout/g,
      ""
    )};path=/;`;
  }
};

const useAffiliateTracking = () => {
  useEffect(() => {
    // Check for existing distributor ID in cookies
    const existingDistributorId = getCookie(AFFILIATE_KEY);
    const existingTimestamp = getCookie(AFFILIATE_TIMESTAMP_KEY);

    if (existingDistributorId && existingTimestamp) {
      const savedTime = parseInt(existingTimestamp, 10);
      const currentTime = Date.now();
      const ageInDays = Math.floor((currentTime - savedTime) / (24 * 60 * 60 * 1000));

      if (currentTime - savedTime <= COOKIE_DURATION_MS) {
        if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
          // eslint-disable-next-line
          console.log(`Affiliate tracking: Distributor ID ${existingDistributorId} loaded from cookie (${ageInDays} days old)`);
        }
      }
    }

    // Check if affiliate cookie is expired
    const checkAndCleanExpiredCookie = () => {
      const timestamp = getCookie(AFFILIATE_TIMESTAMP_KEY);
      if (timestamp) {
        const savedTime = parseInt(timestamp, 10);
        const currentTime = Date.now();

        if (currentTime - savedTime > COOKIE_DURATION_MS) {
          // Cookie has expired, remove it
          if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
            // eslint-disable-next-line
            console.log(`Affiliate tracking: Distributor ID expired and removed`);
          }
          deleteCookie(AFFILIATE_KEY);
          deleteCookie(AFFILIATE_TIMESTAMP_KEY);
        }
      }
    };

    // Extract distributor ID from URL
    const extractDistributorId = () => {
      // First check if there's already a valid distributor ID
      const existingId = getCookie(AFFILIATE_KEY);
      const existingTimestampInner = getCookie(AFFILIATE_TIMESTAMP_KEY);

      if (existingId && existingTimestampInner) {
        const savedTime = parseInt(existingTimestampInner, 10);
        const currentTime = Date.now();

        if (currentTime - savedTime <= COOKIE_DURATION_MS) {
          // Valid distributor ID already exists, don't override
          // Use the cached initial URL params to avoid race conditions
          const attemptedId = initialUrlParams ? initialUrlParams.get("dist") : null;

          if (attemptedId && attemptedId !== existingId) {
            if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
              // eslint-disable-next-line
              console.log(`Affiliate tracking: Attempted to set distributor ID ${attemptedId}, but ID ${existingId} is already locked for this user`);
            }
          }

          // Defer URL cleanup to allow other components to process it first
          if (attemptedId) {
            setTimeout(() => {
              const currentUrlParams = new URLSearchParams(window.location.search);
              currentUrlParams.delete("dist");
              const newUrl = `${window.location.pathname}${currentUrlParams.toString() ? `?${currentUrlParams.toString()}` : ""}${window.location.hash}`;
              window.history.replaceState({}, document.title, newUrl);
            }, 100);
          }
          return; // Exit early, don't override existing ID
        }
      }

      // No valid existing ID, proceed with extraction
      // Use the cached initial URL params to avoid race conditions
      const distributorId = initialUrlParams ? initialUrlParams.get("dist") : null;

      if (distributorId && /^\d+$/.test(distributorId)) {
        // Valid distributor ID found, save to cookies
        if (!process.env.NODE_ENV || process.env.NODE_ENV === "development") {
          // eslint-disable-next-line
          console.log(`Affiliate tracking: Distributor ID ${distributorId} detected and saved`);
        }
        setCookie(AFFILIATE_KEY, distributorId, 30);
        setCookie(AFFILIATE_TIMESTAMP_KEY, Date.now().toString(), 30);

        // Remove the query parameter from URL for cleaner URLs (deferred)
        setTimeout(() => {
          const currentUrlParams = new URLSearchParams(window.location.search);
          currentUrlParams.delete("dist");
          const newUrl = `${window.location.pathname}${currentUrlParams.toString() ? `?${currentUrlParams.toString()}` : ""}${window.location.hash}`;
          window.history.replaceState({}, document.title, newUrl);
        }, 100);
      }
    };

    // Run checks on mount
    checkAndCleanExpiredCookie();
    extractDistributorId();
  }, []);

  // Helper function to get current distributor ID
  const getDistributorId = () => {
    const timestamp = getCookie(AFFILIATE_TIMESTAMP_KEY);
    if (timestamp) {
      const savedTime = parseInt(timestamp, 10);
      const currentTime = Date.now();

      if (currentTime - savedTime <= COOKIE_DURATION_MS) {
        return getCookie(AFFILIATE_KEY);
      }
    }
    return null;
  };

  // Helper function to manually set distributor ID
  const setDistributorId = (distributorId) => {
    if (distributorId && /^\d+$/.test(distributorId)) {
      setCookie(AFFILIATE_KEY, distributorId, 30);
      setCookie(AFFILIATE_TIMESTAMP_KEY, Date.now().toString(), 30);
      return true;
    }
    return false;
  };

  // Helper function to clear distributor ID
  const clearDistributorId = () => {
    deleteCookie(AFFILIATE_KEY);
    deleteCookie(AFFILIATE_TIMESTAMP_KEY);
  };

  return {
    getDistributorId,
    setDistributorId,
    clearDistributorId,
  };
};

export default useAffiliateTracking;
